import React, { useState, useMemo } from 'react';
import { Collapse, Badge } from 'react-bootstrap';
import { ChevronDown, Clock } from 'lucide-react';
import { IssueWithBuilding } from '../../../types/issues';
import { UpcomingMaintenanceItem } from '../../../hooks/management/useUpcomingMaintenance';
import { BuildingCheckWithTemplate } from '../../BuildingChecks/buildingChecksUtils';
import IssueCard from './IssueCard';
import MaintenanceCard from './MaintenanceCard';
import TaskCard, { UnifiedTask } from './TaskCard';
import { formatDateForDisplay } from '../../../Helpers/DateUtils';

interface SnoozedSectionProps {
    issues: IssueWithBuilding[];
    maintenance: UpcomingMaintenanceItem[];
    buildingChecks: BuildingCheckWithTemplate[];
    onIssueClick: (issue: IssueWithBuilding) => void;
    onMaintenanceClick: (item: UpcomingMaintenanceItem) => void;
    onBuildingCheckClick: (check: BuildingCheckWithTemplate) => void;
    /** The check as a task (inspectionToTask with the page's component names) */
    checkToTask: (check: BuildingCheckWithTemplate) => UnifiedTask;
}

/**
 * Collapsible section displaying snoozed items at the bottom of "Att hantera" column
 * Shows count badge and wake date for each item
 * Collapsed by default
 */
const SnoozedSection: React.FC<SnoozedSectionProps> = ({
    issues,
    maintenance,
    buildingChecks,
    onIssueClick,
    onMaintenanceClick,
    onBuildingCheckClick,
    checkToTask
}) => {
    const [isExpanded, setIsExpanded] = useState(false);

    // Combine and sort all snoozed items (memoized)
    type SnoozedItem =
        | { type: 'issue'; item: IssueWithBuilding; snoozedUntil: string }
        | { type: 'maintenance'; item: UpcomingMaintenanceItem; snoozedUntil: string }
        | { type: 'buildingCheck'; item: BuildingCheckWithTemplate; snoozedUntil: string };

    const allItems = useMemo((): SnoozedItem[] => {
        const items: SnoozedItem[] = [
            ...issues.map(item => ({
                type: 'issue' as const,
                item,
                snoozedUntil: item.snoozedUntil || ''
            })),
            ...maintenance.map(item => ({
                type: 'maintenance' as const,
                item,
                snoozedUntil: item.snoozedUntil || ''
            })),
            ...buildingChecks.map(item => ({
                type: 'buildingCheck' as const,
                item,
                snoozedUntil: item.snoozedUntil || ''
            }))
        ];

        // Sort by snoozedUntil (soonest first)
        return items.sort((a, b) => a.snoozedUntil.localeCompare(b.snoozedUntil));
    }, [issues, maintenance, buildingChecks]);

    // Don't render if nothing is snoozed
    if (allItems.length === 0) {
        return null;
    }

    return (
        <div className="snoozed-section mt-3">
            <div
                className="snoozed-section-header"
                onClick={() => setIsExpanded(!isExpanded)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => e.key === 'Enter' && setIsExpanded(!isExpanded)}
            >
                <div className="d-flex align-items-center gap-2">
                    <ChevronDown
                        size={16}
                        className={`snoozed-chevron ${isExpanded ? '' : 'collapsed'}`}
                    />
                    <Clock size={16} className="text-muted" />
                    <span className="snoozed-section-title">Snoozade</span>
                    <Badge bg="secondary" pill className="snoozed-count">
                        {allItems.length}
                    </Badge>
                </div>
            </div>

            <Collapse in={isExpanded}>
                <div className="snoozed-section-content mt-2">
                    {allItems.map((snoozedItem, index) => {
                        const wakeDate = snoozedItem.snoozedUntil;
                        const wakeBadge = (
                            <div className="snoozed-wake-badge mb-1">
                                <small className="text-muted d-flex align-items-center gap-1">
                                    <Clock size={12} />
                                    Vaknar: {formatDateForDisplay(wakeDate)}
                                </small>
                            </div>
                        );

                        switch (snoozedItem.type) {
                            case 'issue':
                                return (
                                    <div key={`issue-${snoozedItem.item.key}`} className="snoozed-item">
                                        {wakeBadge}
                                        <IssueCard
                                            issue={snoozedItem.item}
                                            onClick={() => onIssueClick(snoozedItem.item)}
                                        />
                                    </div>
                                );
                            case 'maintenance':
                                return (
                                    <div key={`maintenance-${snoozedItem.item.key}`} className="snoozed-item">
                                        {wakeBadge}
                                        <MaintenanceCard
                                            item={snoozedItem.item}
                                            onClick={() => onMaintenanceClick(snoozedItem.item)}
                                        />
                                    </div>
                                );
                            case 'buildingCheck':
                                const task = checkToTask(snoozedItem.item);
                                return (
                                    <div key={`check-${snoozedItem.item.buildingId}-${snoozedItem.item.templateId}`} className="snoozed-item">
                                        {wakeBadge}
                                        <TaskCard
                                            task={task}
                                            onClick={() => onBuildingCheckClick(snoozedItem.item)}
                                        />
                                    </div>
                                );
                            default:
                                return null;
                        }
                    })}
                </div>
            </Collapse>
        </div>
    );
};

export default SnoozedSection;
